'use client';

import { useCallback, useEffect, useState } from 'react';

import { Minus, Plus } from 'lucide-react';
import { Slider } from '@/shadcn/ui/slider';

export default function InteractiveSlider({
  sliderMinProfitRate,
  sliderMaxProfitRate,
  sliderAccountSizes,
  isBrazil,
}: {
  sliderMinProfitRate?: number;
  sliderMaxProfitRate?: number;
  sliderAccountSizes?: Array<{ size: number; profitSharePercentage: number }>;
  isBrazil?: boolean;
}) {
  const [selectedAccount, setSelectedAccount] = useState<{
    size: number;
    profitSharePercentage: number;
  }>();
  const [selectedProfitPercentage, setSelectedProfitPercentage] =
    useState<number>(4.3);

  const nextAcc = useCallback(() => {
    if (!sliderAccountSizes || sliderAccountSizes.length === 0) return;

    const currentIndex = sliderAccountSizes.findIndex(
      (each) => each.size === selectedAccount?.size
    );

    setSelectedAccount(
      sliderAccountSizes[
        currentIndex + 1 === sliderAccountSizes.length ? 0 : currentIndex + 1
      ]
    );
  }, [sliderAccountSizes, selectedAccount]);

  const previousAcc = useCallback(() => {
    if (!sliderAccountSizes || sliderAccountSizes.length === 0) return;

    const currentIndex = sliderAccountSizes.findIndex(
      (each) => each.size === selectedAccount?.size
    );

    setSelectedAccount(
      sliderAccountSizes[
        currentIndex === 0 ? sliderAccountSizes.length - 1 : currentIndex - 1
      ]
    );
  }, [sliderAccountSizes, selectedAccount]);

  useEffect(() => {
    if (!sliderAccountSizes || sliderAccountSizes.length === 0) return;

    const defaultAccount = sliderAccountSizes.find((each) => each.size === 50000);
    if (defaultAccount) {
      setSelectedAccount(defaultAccount);
    } else {
      setSelectedAccount(sliderAccountSizes[0]);
    }
  }, [sliderAccountSizes]);

  if (!sliderAccountSizes || sliderAccountSizes.length === 0) {
    return null;
  }

  return (
    <div className="lg:w-[73.376rem] w-[41rem] mx-auto relative">
      <div
        className="w-full  mx-auto rounded-[2.5rem] p-[3rem] lg:px-[5.25rem] lg:py-[4rem] flex flex-col justify-center items-center relative z-[5]"
        style={{
          background: 'linear-gradient(180deg, #103037 0%, #103037 100%)',
        }}
      >
        <h4 className="font-geist text-white text-xl leading-[150%] font-normal mb-[5.25rem] tracking-[-0.12rem]">
          {isBrazil ? 'Simule seus ganhos!' : 'Estimate Your Profits!'}
        </h4>

        <span className="font-geist text-white text-md leading-[150%] font-normal">
          {isBrazil ? 'Saldo da conta' : 'Account size'}
        </span>

        <div className="w-full flex justify-between items-center py-[1.125rem] px-4 bg-white/[0.13] rounded-[4.625rem] mt-[1.5rem] mb-[5rem]">
          <button
            className="h-[5.5rem] w-[5.5rem] min-h-[5.5rem] min-w-[5.5rem] rounded-full bg-white flex justify-center items-center border border-transparent hover:border-black/30 transition-all"
            onClick={() => previousAcc()}
          >
            <Minus className="text-black" width={18} height={18} />
          </button>

          <span className="font-geist text-white text-[2.75rem] leading-[150%] font-medium">
            ${' '}
            {new Intl.NumberFormat('en-US', {
              notation: 'standard',
              compactDisplay: 'long',
            }).format(selectedAccount?.size ?? 0)}
          </span>

          <button
            className="h-[5.5rem] w-[5.5rem] min-h-[5.5rem] min-w-[5.5rem] rounded-full bg-white flex justify-center items-center border border-transparent hover:border-black/30 transition-all"
            onClick={() => nextAcc()}
          >
            <Plus className="text-black" width={18} height={18} />
          </button>
        </div>

        <span className="font-geist text-white text-[2.25rem] leading-[150%] font-normal">
          {isBrazil ? 'Ganho mensal' : 'Profit Rate'}
        </span>

        <div className="w-fit flex justify-between items-center p-[1rem] bg-white/[0.13] rounded-[4.625rem] mt-[1.5rem] mb-[3.38rem]">
          <span className="font-geist text-white text-[2.25rem] leading-[150%] font-medium px-[2rem]">
            {selectedProfitPercentage} %
          </span>
        </div>

        <Slider
          defaultValue={[selectedProfitPercentage]}
          min={sliderMinProfitRate ?? 0}
          max={sliderMaxProfitRate ?? 10}
          step={0.1}
          onValueChange={(value) => setSelectedProfitPercentage(value[0])}
        />

        {/* <Image
          src={dootedLineMobile}
          alt="dooted line"
          className="w-full my-[4rem] lg:my-[5rem] opacity-60 lg:opacity-80"
        /> */}

        <svg
          className="w-full h-auto my-[4.25rem]"
          xmlns="http://www.w3.org/2000/svg"
          width="496"
          height="1"
          fill="none"
          viewBox="0 0 496 1"
        >
          <path
            stroke="#fff"
            strokeDasharray="8 8"
            strokeOpacity="0.12"
            d="M0 .5h495.013"
          ></path>
        </svg>

        <span className="font-geist text-white text-[2.25rem] leading-[150%] font-[400]">
          {isBrazil ? 'Você recebe' : 'Your Share'}{' '}
          <span className="font-[700]">
            ({selectedAccount?.profitSharePercentage}%)
          </span>
        </span>

        <h3 className="font-sfpro text-white text-[4rem] lg:text-[5.5rem] leading-[150%] font-[600] mt-[1rem] mb-0">
          ${' '}
          {new Intl.NumberFormat('en-US', {
            notation: 'standard',
            compactDisplay: 'long',
          }).format(
            (((selectedAccount?.size ?? 0) * selectedProfitPercentage) / 100) *
              0.8
          )}
          <span className="text-[2rem] font-[400] tracking-normal opacity-50">
            {' '}
            {isBrazil ? 'mês' : 'month'}
          </span>
        </h3>
        {/*  */}
      </div>

      <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-[rgba(72,146,104,0.55)] z-[1] lg:w-[88rem] w-[47rem] h-[43.75rem] lg:h-[60.375rem] rounded-[2.57rem] backdrop-blur-[1.875rem]"></div>

      <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-[#103037] z-[0] w-[53rem] lg:w-[100rem] lg:h-[50.625rem] h-[36.69rem] rounded-[2.36rem]"></div>
    </div>
  );
}
